import {
  IContactRequestSchema,
  IContactRespSchema,
  IInvoiceItemRespSchema,
  IInvoiceTermsSettingsSchema,
  ILegalEntityResponseSchema,
  InvoiceTypes,
  IOfferingRes,
  IOneTimeOfferingProductRequestSchema,
  IOneTimeOfferingRequestSchemaUi,
  NetTermsEnum,
  ProductResWithMandatory,
} from '@monetize/types/app';
import { Maybe } from '@monetize/types/common';
import {
  getCurrencyDecimals,
  sortByProductPosition,
} from '@monetize/utils/core';
import { roundNumberToDecimal } from '../../../utils/numberUtils';

/**
 * A line's amount as billing-platform will save it: quantity × unit price,
 * rounded to the currency's decimals (whole yen for JPY). The unit price itself
 * keeps its sub-unit digits.
 */
export const getOneTimeInvoiceLineAmount = (
  quantity: number,
  unitPrice: number,
  currency: Maybe<string>,
) => roundNumberToDecimal(quantity * unitPrice, getCurrencyDecimals(currency));

export const getDuplicateInvoiceActionTitle = (invoiceType: InvoiceTypes) =>
  invoiceType === InvoiceTypes.ONETIME
    ? 'Clone Invoice'
    : 'Clone as Onetime Invoice';

/**
 * Summary invoice responses carry contact snapshots without ids;
 * only full invoice responses include the contact id.
 */
export const getInvoiceContactId = (
  contact?: Maybe<IContactRespSchema | IContactRequestSchema>,
): string | undefined => (contact && 'id' in contact ? contact.id : undefined);

/** Header fields the invoice defaults from its bill group. */
export const REFERENCE_NUMBER_FIELDS = [
  'purchaseOrderNumber',
  'vatNumber',
  'registrationNumber',
] as const;

export type ReferenceNumberField = (typeof REFERENCE_NUMBER_FIELDS)[number];

type ReferenceNumbers = Partial<Record<ReferenceNumberField, Maybe<string>>>;

/**
 * Works out which reference numbers to write when the selected bill group is
 * derived, returning only the fields that need changing.
 *
 * On the first selection just the empty fields are filled, so an invoice being
 * duplicated keeps the values copied from its source invoice. Once the user
 * switches bill group the fields follow the new selection outright — a bill
 * group with no PO or VAT number clears whatever the previous one supplied,
 * rather than leaving a stale value attributed to the wrong bill group.
 */
/**
 * Net terms follows the selected bill group on the same terms as the reference
 * numbers, with one difference: it is required, so a bill group carrying no net
 * terms falls back to the default rather than clearing the field and leaving
 * the form invalid. Returns undefined when nothing needs to change.
 */
export const getBillGroupNetTerms = ({
  billGroupNetTerms,
  currentNetTerms,
  isInitialSelection,
}: {
  billGroupNetTerms?: Maybe<NetTermsEnum>;
  currentNetTerms?: Maybe<NetTermsEnum>;
  isInitialSelection: boolean;
}): NetTermsEnum | undefined => {
  // On the initial selection an existing value wins, so a duplicated invoice
  // keeps the net terms copied from its source invoice.
  if (isInitialSelection && currentNetTerms) {
    return undefined;
  }
  const nextNetTerms = billGroupNetTerms ?? NetTermsEnum.NET_30;
  return nextNetTerms === currentNetTerms ? undefined : nextNetTerms;
};

export const getBillGroupReferenceNumbers = ({
  billGroup,
  currentValues,
  isInitialSelection,
}: {
  billGroup: ReferenceNumbers;
  currentValues: ReferenceNumbers;
  isInitialSelection: boolean;
}): Partial<Record<ReferenceNumberField, string>> =>
  REFERENCE_NUMBER_FIELDS.reduce<Partial<Record<ReferenceNumberField, string>>>(
    (updates, field) => {
      const billGroupValue = billGroup[field] || '';
      const currentValue = currentValues[field] || '';

      if (isInitialSelection) {
        if (!currentValue && billGroupValue) {
          updates[field] = billGroupValue;
        }
      } else if (billGroupValue !== currentValue) {
        updates[field] = billGroupValue;
      }
      return updates;
    },
    {},
  );

/**
 * Invoice terms default to the legal entity's terms for the invoice currency,
 * then the tenant default, and are left blank when neither is configured.
 *
 * Legal entity terms are only consulted behind the flag that exposes them in
 * settings, and only an active entry covering the currency counts as
 * configured — in either case the tenant default is the next best thing rather
 * than a reason to leave the field empty. The tenant default in turn only
 * applies while it is active, matching what settings lets a tenant save.
 */
export const getDefaultInvoiceTerms = ({
  legalEntity,
  currency,
  invoiceTermsSettings,
  enableLegalEntityInvoiceTerms,
}: {
  legalEntity?: Maybe<Pick<ILegalEntityResponseSchema, 'options'>>;
  currency?: Maybe<string>;
  invoiceTermsSettings?: Maybe<IInvoiceTermsSettingsSchema>;
  enableLegalEntityInvoiceTerms: boolean;
}): string => {
  if (enableLegalEntityInvoiceTerms && currency) {
    const currencyTerms =
      legalEntity?.options?.invoiceTerms?.currencyTerms?.find(
        ({ active, currencies, terms }) =>
          active && !!terms && currencies.includes(currency),
      );
    if (currencyTerms) {
      return currencyTerms.terms;
    }
  }

  const { invoiceTermsActive, invoiceTerms } = invoiceTermsSettings ?? {};
  return invoiceTermsActive && invoiceTerms ? invoiceTerms : '';
};

/**
 * A product ends up on the invoice when it is mandatory on the offering or when
 * it was picked in the Manage Products drawer. Mandatory products are seeded
 * with `isSelected: true`, but the flag is treated as advisory here so the
 * rendered rows, the running total and the submitted line items can never
 * disagree about which products are billed.
 */
export const isOneTimeInvoiceProductSelected = ({
  isSelected,
  isMandatory,
}: {
  isSelected?: Maybe<boolean>;
  isMandatory?: Maybe<boolean>;
}): boolean => !!isSelected || !!isMandatory;

export const getProductObj = ({
  id,
  description,
  isMandatory,
  isSelected,
}: ProductResWithMandatory): IOneTimeOfferingProductRequestSchema => {
  return {
    productId: id!,
    quantity: 1,
    unitPrice: 0,
    productDescription: description || '',
    isMandatory,
    isSelected,
  };
};

/**
 * Builds a single product row for the one-time invoice form. Without an
 * invoice item this is just the catalog default (used to seed products not
 * on the source invoice). With one, the quantity, price, and description
 * actually billed on that line win, and the row is marked selected - this is
 * the one place quantity/price/description get carried over from a cloned
 * invoice, whether it's a product's only occurrence or a repeat.
 */
const buildInvoiceItemProductRow = ({
  offeringProduct,
  invoiceItem,
}: {
  offeringProduct: IOfferingRes['offeringProducts'][number];
  invoiceItem?: IInvoiceItemRespSchema;
}): IOneTimeOfferingProductRequestSchema => {
  const product = getProductObj({
    ...offeringProduct.product,
    isMandatory: offeringProduct.isMandatory,
    isSelected: offeringProduct.isMandatory,
    position: offeringProduct.position,
    isPriceTierLockEligible: offeringProduct.isPriceTierLockEligible,
  });

  if (!invoiceItem) {
    return product;
  }

  const { quantity, amount, description } = invoiceItem;
  return {
    ...product,
    isSelected: true,
    quantity,
    unitPrice: quantity > 0 ? roundNumberToDecimal(amount / quantity) : 0,
    productDescription: description || product.productDescription,
  };
};

export interface MapInvoiceItemsToOfferingsResult {
  offerings: IOneTimeOfferingRequestSchemaUi[];
  /** Human readable labels for invoice items that could not be carried over */
  skippedItems: string[];
}

/**
 * Maps the line items of a source invoice onto the one-time invoice form
 * offerings structure (used when duplicating an invoice as a one-time invoice).
 *
 * Products present on the source invoice are selected with their quantity,
 * unit price, description, and billing period carried over from their
 * invoice line, in the invoice's original line order. The unit price is
 * derived as `amount / quantity` (the post-discount amount actually billed
 * on the source invoice line) since the one-time invoice form has no
 * separate discount field - this reproduces the exact dollar total from
 * that line. An offering block's dates come from the earliest invoiced line
 * placed in it (the form has one start/end date per block, not per product).
 *
 * A product invoiced more than once (e.g. a mid-cycle quantity or price
 * change) is never summed into one blended row - each occurrence after the
 * first becomes its own single-product offering block, immediately
 * following the offering's primary block, so the clone keeps as many rows
 * as the source invoice had lines.
 *
 * A catalog product that's mandatory on the offering but was never actually
 * billed on the source invoice is left out of the cloned block entirely,
 * rather than seeded like the rest of the catalog - mandatory products are
 * always billed once present (see `isOneTimeInvoiceProductSelected`), so
 * including it would silently add a line that wasn't on the original
 * invoice. Items whose offering or product no longer exists in the catalog
 * are reported in `skippedItems`.
 */
export const mapInvoiceItemsToOfferings = ({
  invoiceItems,
  offeringsById,
}: {
  invoiceItems: IInvoiceItemRespSchema[];
  offeringsById: Record<string, IOfferingRes>;
}): MapInvoiceItemsToOfferingsResult => {
  const skippedItems: string[] = [];

  // A nullish lineNumber (legacy invoices) sorts as if it were the first line.
  const sortedInvoiceItems = [...invoiceItems].sort(
    (a, b) => (a.lineNumber ?? 0) - (b.lineNumber ?? 0),
  );

  // Group items by offering, preserving the order offerings first appear in
  // the now line-number-sorted invoice.
  const itemsByOfferingId = new Map<string, IInvoiceItemRespSchema[]>();
  sortedInvoiceItems.forEach((item) => {
    const group = itemsByOfferingId.get(item.offeringId);
    if (group) {
      group.push(item);
    } else {
      itemsByOfferingId.set(item.offeringId, [item]);
    }
  });

  const offerings: IOneTimeOfferingRequestSchemaUi[] = [];
  itemsByOfferingId.forEach((items, offeringId) => {
    const offering = offeringsById[offeringId];
    if (!offering || offering.offeringProducts.length <= 0) {
      skippedItems.push(
        ...items.map(
          ({ offeringName, productName }) => `${offeringName} - ${productName}`,
        ),
      );
      return;
    }

    const offeringProductsByProductId = new Map(
      offering.offeringProducts.map((offeringProduct) => [
        offeringProduct.product.id,
        offeringProduct,
      ]),
    );

    // Seed every catalog product so unselected ones still show in "Manage
    // Products", except a mandatory one that wasn't actually invoiced - it
    // can't be seeded merely unselected, since mandatory products are always
    // billed once present. Each invoiced product's slot is overwritten below.
    const invoicedProductIds = new Set(items.map(({ productId }) => productId));
    const primaryProducts = sortByProductPosition(offering.offeringProducts)
      .filter(
        (offeringProduct) =>
          !offeringProduct.isMandatory ||
          invoicedProductIds.has(offeringProduct.product.id),
      )
      .map((offeringProduct) =>
        buildInvoiceItemProductRow({ offeringProduct }),
      );

    const repeatOfferings: IOneTimeOfferingRequestSchemaUi[] = [];
    const placedProductIds = new Set<string>();

    items.forEach((item) => {
      const offeringProduct = offeringProductsByProductId.get(item.productId);
      if (!offeringProduct) {
        skippedItems.push(`${item.offeringName} - ${item.productName}`);
        return;
      }

      const row = buildInvoiceItemProductRow({
        offeringProduct,
        invoiceItem: item,
      });

      if (placedProductIds.has(item.productId)) {
        repeatOfferings.push({
          offeringId: offering.id,
          offeringDescription: offering.name,
          startDate: item.periodStartDate,
          endDate: item.periodEndDate,
          products: [row],
          metadata: { loading: false, offering },
        });
        return;
      }

      placedProductIds.add(item.productId);
      const primaryProductIndex = primaryProducts.findIndex(
        ({ productId }) => productId === item.productId,
      );
      primaryProducts[primaryProductIndex] = row;
    });

    offerings.push(
      {
        offeringId: offering.id,
        offeringDescription: offering.name,
        startDate: items[0].periodStartDate,
        endDate: items[0].periodEndDate,
        products: primaryProducts,
        metadata: { loading: false, offering },
      },
      ...repeatOfferings,
    );
  });

  return { offerings, skippedItems };
};
